import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEmailStore } from '@/stores/useEmailStore';
import { RefreshCw } from 'lucide-react';

const STATUS_OPTIONS = [
  'queued',
  'processing',
  'failed',
  'dead_letter',
  'suppressed',
  'consent_blocked',
];

export function EmailQueueTable() {
  const { queue, queueLoading, fetchQueue } = useEmailStore();
  const [status, setStatus] = useState('all');

  useEffect(() => {
    fetchQueue({ status: status === 'all' ? null : status });
  }, [fetchQueue, status]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Outbox queue</CardTitle>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => fetchQueue({ status: status === 'all' ? null : status })}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {queueLoading && (!queue || queue.length === 0) ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !queue?.length ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No outbox rows match this filter.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Template</th>
                  <th className="py-2 pr-3 font-medium">Source</th>
                  <th className="py-2 pr-3 font-medium">Attempts</th>
                  <th className="py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      <Badge variant="secondary">{row.status}</Badge>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{row.template_key}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{row.source}</td>
                    <td className="py-2 pr-3">
                      {row.attempt_count}/{row.max_attempts}
                    </td>
                    <td className="py-2 text-muted-foreground">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
